import { IconButton, Menu } from '@affine/component';
import {
  SettingsChip,
  type SettingsChipTone,
} from '@affine/component/setting-components';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { DisclosureChevron } from '@affine/core/components/animation/disclosure-chevron';
import { CachedUserAvatar } from '@affine/core/components/cached-user-avatar';
import { useI18n } from '@affine/i18n';
import { MoreVerticalIcon } from '@blocksuite/icons/rc';
import {
  type FC,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
} from 'react';

import * as entityStyles from './settings-entity.css';
import { useRenderedWhileOpen } from './use-rendered-while-open';

export const SettingsEntityCard: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <article className={entityStyles.card}>{children}</article>;
};

export const SettingsEntityList: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <div className={entityStyles.list}>{children}</div>;
};

/**
 * The always visible line of a list entry. With `onToggle`, a click anywhere on
 * the line opens or closes its details; the keyboard reaches the same through
 * the title part. Without it the line only shows the entry.
 */
export const SettingsEntitySummary: FC<{
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  meta?: ReactNode;
  aside?: ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  dataTestId?: string;
}> = ({
  leading,
  title,
  subtitle,
  badges,
  meta,
  aside,
  expanded,
  onToggle,
  dataTestId,
}) => {
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Keys pressed inside a control of the line belong to that control.
      if (event.target !== event.currentTarget) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      onToggle?.();
    },
    [onToggle]
  );

  return (
    <div
      className={entityStyles.summary}
      onClick={onToggle}
      data-expanded={onToggle ? Boolean(expanded) : undefined}
      data-testid={dataTestId}
    >
      <div
        className={entityStyles.summaryMain}
        {...(onToggle
          ? {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': Boolean(expanded),
              onKeyDown,
            }
          : {})}
      >
        <div className={entityStyles.titleRow}>
          {onToggle ? (
            <DisclosureChevron
              expanded={Boolean(expanded)}
              className={entityStyles.expandIcon}
            />
          ) : null}
          {leading}
          <div className={entityStyles.titleBlock}>
            <div className={entityStyles.title}>{title}</div>
            {subtitle ? (
              <div className={entityStyles.subtitle}>{subtitle}</div>
            ) : null}
          </div>
          {badges ? (
            <div className={entityStyles.badgeRow}>{badges}</div>
          ) : null}
        </div>

        {meta ? <div className={entityStyles.meta}>{meta}</div> : null}
      </div>
      {aside ? <div className={entityStyles.summaryAside}>{aside}</div> : null}
    </div>
  );
};

const keepFromLine = (event: MouseEvent) => event.stopPropagation();

/** The menu at the end of a list entry; it holds every action on that entry. */
export const SettingsEntityMenu: FC<{
  /** Names the button for screen readers, e.g. "Actions". */
  label: string;
  items: ReactNode;
  disabled?: boolean;
  testId?: string;
}> = ({ label, items, disabled, testId }) => {
  return (
    <Menu items={items} contentOptions={{ align: 'end' }}>
      <IconButton
        aria-label={label}
        disabled={disabled}
        data-testid={testId}
        onClick={keepFromLine}
      >
        <MoreVerticalIcon />
      </IconButton>
    </Menu>
  );
};

export const SettingsEntityMessage: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <div className={entityStyles.message}>{children}</div>;
};

export const SettingsEntityAvatar: FC<{
  size?: number;
  userId?: string | null;
  name?: string | null;
  url?: string | null;
  fallbackName?: string;
}> = ({ size = 28, userId, name, url, fallbackName }) => {
  const t = useI18n();
  return (
    <CachedUserAvatar
      size={size}
      userId={userId}
      name={
        name || fallbackName || t['com.streamlain.settings.ui.person.unnamed']()
      }
      url={url ?? null}
    />
  );
};

export const SettingsExpandableItem: FC<{
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  badges?: ReactNode;
  meta?: ReactNode;
  aside?: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  dataTestId?: string;
  children?: ReactNode;
}> = ({
  leading,
  title,
  subtitle,
  badges,
  meta,
  aside,
  expanded,
  onToggle,
  dataTestId,
  children,
}) => {
  // The region follows the height of the details while their content loads.
  const { rendered, onSettled } = useRenderedWhileOpen(expanded);

  return (
    <SettingsEntityCard>
      <SettingsEntitySummary
        leading={leading}
        title={title}
        subtitle={subtitle}
        badges={badges}
        meta={meta}
        aside={aside}
        expanded={expanded}
        onToggle={onToggle}
        dataTestId={dataTestId}
      />
      {children ? (
        <AnimatedRegion
          expanded={expanded}
          animateContent
          onSettled={onSettled}
        >
          {rendered ? (
            <div className={entityStyles.expanded}>{children}</div>
          ) : null}
        </AnimatedRegion>
      ) : null}
    </SettingsEntityCard>
  );
};

export const SettingsEntityIdentityAside: FC<{
  avatar: ReactNode;
  name: ReactNode;
}> = ({ avatar, name }) => {
  return (
    <div className={entityStyles.identityAside}>
      {avatar}
      <span className={entityStyles.identityName}>{name}</span>
    </div>
  );
};

const BADGE_TONES = {
  soft: 'grey',
  active: 'green',
  error: 'red',
} as const satisfies Record<string, SettingsChipTone>;

export const SettingsBadge: FC<{
  variant?: keyof typeof BADGE_TONES;
  children: ReactNode;
}> = ({ variant = 'soft', children }) => {
  return <SettingsChip tone={BADGE_TONES[variant]}>{children}</SettingsChip>;
};

export const SettingsMetaItem: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return <span className={entityStyles.metaItem}>{children}</span>;
};
